import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const isStandalonePWA = () =>
  (typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof window !== "undefined" && window.navigator?.standalone === true);

const PWAStartGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const redirectIfNeeded = () => {
      if (!isStandalonePWA()) return;
      if (locationRef.current.pathname === "/app") return;
      navigate("/app", { replace: true });
    };

    // 1. Initial mount: PWA opened fresh (Open in App, icon launch, post-install
    //    window). Redirect now if we landed anywhere other than /app.
    redirectIfNeeded();

    // 2. Mobile install path: Chrome transitions the same tab from browser to
    //    standalone without reloading, so this component doesn't remount.
    //    Watch the media query and redirect when display-mode flips.
    const mql = window.matchMedia?.("(display-mode: standalone)");
    if (!mql) return undefined;

    const onDisplayModeChange = (e) => {
      if (e.matches) redirectIfNeeded();
    };

    if (mql.addEventListener) {
      mql.addEventListener("change", onDisplayModeChange);
    } else if (mql.addListener) {
      mql.addListener(onDisplayModeChange);
    }

    // 3. `appinstalled` is the most reliable signal that an install just
    //    completed. Belt-and-suspenders against the rare case where Chrome
    //    keeps the user's pre-install URL after standalone transition.
    const onAppInstalled = () => redirectIfNeeded();
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", onDisplayModeChange);
      } else if (mql.removeListener) {
        mql.removeListener(onDisplayModeChange);
      }
      window.removeEventListener("appinstalled", onAppInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default PWAStartGate;
